import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import AccountingWrapper from "../../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../../Components/v2/Accounting/ReportRevisionNotice";
import FormDate from "../../../../../Components/v2/Form/FormDate";
import Modal from "../../../../../Components/v2/Modal";
import LoadErrorState from "../../../../../Components/v2/LoadErrorState";

import { cancelAPIRequest, getAccountingFileName } from "../../../../../Utils";
import { fromPeriod, toPeriod } from "../../../../../Utils/calendarDate";
import {
  clearExportVatRecap,
  clearVatRecap,
  requestExportVatRecap,
  requestVatRecap,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../../Context";
import { EXPORTS } from "../../../../../Constants/accountingReport";

import {
  creditedBillTotals,
  isCredited,
  isEmptyRecap,
  ledgerDifferences,
  netStatusView,
  periodFromParam,
} from "./utils";
import {
  EmptyRecap,
  InputTable,
  JournalTable,
  LedgerNotice,
  OutputTable,
  ReviewPanel,
  SummaryCards,
  SummarySkeleton,
} from "./sections";
import ErrorDialog from "../../../../../Components/v2/ErrorDialog";

const AccountingVatRecap = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();
  const businessID = user?.mainBusiness?.id;

  // The month lives in the URL so a link, a refresh or Back from an invoice
  // lands on the same tax period.
  const [searchParams, setSearchParams] = useSearchParams();
  const period = periodFromParam(searchParams.get("period"));

  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();
  const {
    request,
    vatRecap,
    vatRecapLoading,
    vatRecapError,
    vatRecapExportLoading,
    vatRecapExportError,
    vatRecapExportFile,
  } = useAccountingState();

  // Picking a month clears the previous one's recap (REQUEST_VAT_RECAP), so
  // an empty recap with no error is always a load in flight.
  const recap = vatRecap;
  const loading = vatRecapLoading || (!recap && !vatRecapError);

  const bills = useMemo(() => recap?.input?.bills || [], [recap]);
  const inputTotals = useMemo(() => creditedBillTotals(bills), [bills]);
  const creditedBillCount = bills.filter(isCredited).length;
  const view = netStatusView(recap?.net);
  const differences = ledgerDifferences(recap?.ledger);

  const monthStart = dayjs(fromPeriod(period)).startOf("month").toDate();
  const monthEnd = dayjs(fromPeriod(period)).endOf("month").toDate();

  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearVatRecap(accountingDispatch);
      clearExportVatRecap(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecap = () => {
    if (!businessID) return;
    if (request) cancelAPIRequest(request);
    requestVatRecap(accountingDispatch, { businessID, period });
  };

  useEffect(() => {
    loadRecap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessID, period]);

  useEffect(() => {
    if (vatRecapExportFile?.key) downloadFile(vatRecapExportFile.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatRecapExportFile]);

  useEffect(() => {
    if (vatRecapExportError) setShowExportError(true);
  }, [vatRecapExportError]);

  const handlePeriodChange = (date) => {
    if (!date) return;
    const next = new URLSearchParams(searchParams);
    next.set("period", toPeriod(date));
    setSearchParams(next, { replace: true });
  };

  const handleExport = (exportType) => {
    if (!businessID) return;
    requestExportVatRecap(accountingDispatch, {
      businessID,
      period,
      exportType,
    });
  };

  const onDownloadProgress = ({ transferredBytes, totalBytes }) => {
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      downloadBlob(result.body, getAccountingFileName(key));
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportVatRecap(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  const renderBody = () => {
    if (vatRecapError && !vatRecapLoading) {
      return (
        <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <LoadErrorState error={vatRecapError} onRetry={loadRecap} />
        </div>
      );
    }

    if (loading) {
      return (
        <>
          <SummarySkeleton />
          <OutputTable loading />
          <InputTable loading />
        </>
      );
    }

    return (
      <>
        <SummaryCards
          recap={recap}
          view={view}
          creditedBillCount={creditedBillCount}
        />
        <LedgerNotice differences={differences} />
        <ReviewPanel key={period} review={recap.review || []} />
        {isEmptyRecap(recap) ? (
          <EmptyRecap period={period} />
        ) : (
          <>
            <div className="space-y-4">
              <OutputTable output={recap.output} />
              <JournalTable
                title={t("reports.vatRecap.output.journals")}
                journals={recap.output?.journals}
              />
            </div>
            <div className="space-y-4">
              <InputTable
                bills={bills}
                totals={inputTotals}
                period={period}
              />
              <JournalTable
                title={t("reports.vatRecap.input.journals")}
                journals={recap.input?.journals}
              />
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="reports.vatRecap.name"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: vatRecapExportLoading,
          }}
        />
        <ReportRevisionNotice
          businessID={businessID}
          startDate={monthStart}
          endDate={monthEnd}
          className="border-b border-gray-200"
        />

        <div className="space-y-6 px-4 py-5 sm:px-6 lg:px-8">
          <div className="w-full sm:max-w-xs">
            <FormDate
              id="vat-recap-period"
              label="reports.vatRecap.period"
              selected={fromPeriod(period)}
              onChange={handlePeriodChange}
              showMonthYearPicker
              dateFormat="MMM yyyy"
            />
          </div>

          {renderBody()}
        </div>

        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) downloadRef.current.cancel();
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {vatRecapExportError && (
          <ErrorDialog
            open={showExportError}
            error={vatRecapExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportVatRecap(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingVatRecap;
